import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faRefresh, faDownload, faTrash, faUpload,
    faFilePdf, faFileWord, faFileExcel, faFileImage, faFile, faSpinner, faEye,
    faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import PdfPreviewModal from '../shared/PdfPreviewModal';
import '../../components/DocumentPanel.css';
import { fetchInvoiceDocuments } from '../../services/invoicingService';

const getFileIcon = (filename) => {
    if (!filename) return faFile;
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return faFilePdf;
    if (['doc', 'docx'].includes(ext)) return faFileWord;
    if (['xls', 'xlsx'].includes(ext)) return faFileExcel;
    if (['png', 'jpg', 'jpeg', 'tiff', 'tif', 'bmp', 'webp'].includes(ext)) return faFileImage;
    return faFile;
};

const isPdfFile = (filename) => {
    if (!filename) return false;
    return filename.split('.').pop().toLowerCase() === 'pdf';
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        let date;
        if (typeof dateString === 'string') {
            const netDateMatch = dateString.match(/\/Date\((\d+)\)\//);
            if (netDateMatch) {
                date = new Date(parseInt(netDateMatch[1], 10));
            } else {
                date = new Date(dateString.replace(/Z+$/, 'Z').trim());
            }
        } else {
            date = new Date(dateString);
        }
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    } catch {
        return '';
    }
};

const InvoiceDocumentPanel = ({ isOpen, onClose, invoiceId, showSuccess, showError, onDocumentCountChange }) => {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // "could not reach the archive" is a different fact from "there are no
    // documents". Showing the empty state for a failed load told the user this
    // invoice has nothing attached, which is the worst possible wrong answer.
    const [loadError, setLoadError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [showPdfPreview, setShowPdfPreview] = useState(false);
    const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
    const [previewFileName, setPreviewFileName] = useState('');
    const panelRef = useRef(null);
    const fileInputRef = useRef(null);

    const getFieldValue = (doc, fieldName) => {
        const field = (doc.Fields || []).find(f => f.FieldName === fieldName);
        return field?.Item || null;
    };

    // Belt and braces. FlashMessageProvider now memoizes showError, so this
    // callback is stable on its own -- but a single un-memoized function in a
    // dep array here is enough to turn a failing fetch into an unbounded
    // request loop (seen live 2026-08-27, hundreds of calls per second while
    // DocuWare was down). Reading it through a ref means this panel cannot
    // loop even if a provider regresses.
    const showErrorRef = useRef(showError);
    useEffect(() => { showErrorRef.current = showError; }, [showError]);

    const fetchDocuments = useCallback(async () => {
        if (!invoiceId) return;
        setIsLoading(true);
        setLoadError(null);
        try {
            const docs = await fetchInvoiceDocuments(invoiceId);
            setDocuments(docs);
            if (onDocumentCountChange) onDocumentCountChange(docs.length);
        } catch (error) {
            console.error('Error fetching invoice documents:', error);
            setLoadError('Could not reach the document archive.');
            if (showErrorRef.current) showErrorRef.current('Failed to load invoice documents');
        } finally {
            setIsLoading(false);
        }
    }, [invoiceId, onDocumentCountChange]);

    useEffect(() => {
        if (invoiceId && isOpen) fetchDocuments();
    }, [invoiceId, isOpen, fetchDocuments]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (!isOpen || showPdfPreview || deleteTarget) return;
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose, showPdfPreview, deleteTarget]);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (deleteTarget || showPdfPreview) return;
            if (panelRef.current && !panelRef.current.contains(e.target) && isOpen) onClose();
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen, onClose, deleteTarget, showPdfPreview]);

    const handleDownload = async (docId, filename) => {
        try {
            const response = await axiosInstance.get(
                `/api/docuware/invoice-documents/download/${docId}/`,
                { responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename || 'document');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            if (showError) showError('Failed to download document');
        }
    };

    const handlePreview = async (docId, filename) => {
        try {
            const response = await axiosInstance.get(
                `/api/docuware/invoice-documents/download/${docId}/`,
                { responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            setPreviewPdfUrl(url);
            setPreviewFileName(filename || 'document.pdf');
            setShowPdfPreview(true);
        } catch (error) {
            console.error('Preview failed:', error);
            if (showError) showError('Failed to load document preview');
        }
    };

    const handleClosePreview = () => {
        setShowPdfPreview(false);
        if (previewPdfUrl) {
            window.URL.revokeObjectURL(previewPdfUrl);
            setPreviewPdfUrl(null);
        }
        setPreviewFileName('');
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await axiosInstance.delete(`/api/docuware/invoice-documents/delete/${deleteTarget.id}/`);
            if (showSuccess) showSuccess('Document deleted');
            setDeleteTarget(null);
            fetchDocuments();
        } catch (error) {
            console.error('Delete failed:', error);
            if (showError) showError('Failed to delete document');
        }
    };

    const handleManualUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !invoiceId) return;

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            if (showError) showError('File size exceeds 10MB limit');
            return;
        }

        setUploading(true);
        try {
            const recordResponse = await axiosInstance.post('/api/docuware/invoice-documents/create-record/', {
                invoice_id: invoiceId,
                subject: file.name,
                invoice_document_type: 'Captured Invoice Files',
            });

            const docId = recordResponse.data.document_id;

            const formData = new FormData();
            formData.append('file', file);
            await axiosInstance.post(`/api/docuware/invoice-documents/upload-file/${docId}/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (showSuccess) showSuccess('Document uploaded successfully');
            fetchDocuments();
        } catch (error) {
            console.error('Upload failed:', error);
            if (showError) showError('Failed to upload document');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="document-panel-overlay">
            <div className="document-panel" ref={panelRef}>
                <div className="document-panel-header">
                    <div style={{ flex: 1 }}>
                        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                            Captured Invoice Files
                        </h2>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', opacity: 0.85 }}>
                            {loadError
                                ? 'Unavailable'
                                : `${documents.length} document${documents.length !== 1 ? 's' : ''}`}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                            className="document-panel-action-btn"
                            onClick={fetchDocuments}
                            disabled={isLoading}
                            title="Refresh"
                            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer' }}
                        >
                            <FontAwesomeIcon icon={faRefresh} spin={isLoading} />
                        </button>
                        <label
                            className="document-panel-action-btn"
                            title="Upload document"
                            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                        >
                            <FontAwesomeIcon icon={uploading ? faSpinner : faUpload} spin={uploading} />
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleManualUpload}
                                style={{ display: 'none' }}
                                accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.webp"
                                disabled={uploading}
                            />
                        </label>
                        <button
                            onClick={onClose}
                            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '6px', fontSize: '1.1rem' }}
                            title="Close"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                <div className="document-panel-content" style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
                    {isLoading && documents.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
                            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }} />
                            <p>Loading documents...</p>
                        </div>
                    ) : loadError ? (
                        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#991b1b' }}>
                            <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '2rem', marginBottom: '0.75rem' }} />
                            <p style={{ margin: 0, fontWeight: 600 }}>{loadError}</p>
                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                                This invoice may still have documents. Try Refresh in a moment.
                            </p>
                        </div>
                    ) : documents.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
                            <FontAwesomeIcon icon={faFile} style={{ fontSize: '2rem', marginBottom: '0.75rem', opacity: 0.4 }} />
                            <p style={{ margin: 0, fontWeight: 500 }}>No documents found</p>
                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
                                Documents captured during invoice creation will appear here.
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {documents.map((doc) => {
                                const filename = getFieldValue(doc, 'SUBJECT') || 'Unknown Document';
                                const dateUploaded = getFieldValue(doc, 'DATE_UPLOADED');
                                const supplierName = getFieldValue(doc, 'SUPPLIER_NAME');
                                const supplierInvNo = getFieldValue(doc, 'SUPPLIER_INVOICE_NUMBER');

                                return (
                                    <div
                                        key={doc.Id}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '0.75rem',
                                            padding: '0.75rem 1rem', background: 'white',
                                            borderRadius: '8px', border: '1px solid #e5e7eb',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={getFileIcon(filename)}
                                            style={{ fontSize: '1.25rem', color: '#3b82f6', flexShrink: 0 }}
                                        />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 500, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {filename}
                                            </div>
                                            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '2px' }}>
                                                {formatDate(dateUploaded)}
                                                {supplierName ? ` - ${supplierName}` : ''}
                                                {supplierInvNo ? ` (${supplierInvNo})` : ''}
                                            </div>
                                        </div>
                                        {isPdfFile(filename) && (
                                            <button
                                                onClick={() => handlePreview(doc.Id, filename)}
                                                style={{ background: 'none', border: 'none', color: '#4a6b25', cursor: 'pointer', padding: '4px 6px', borderRadius: '4px' }}
                                                title="Preview"
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDownload(doc.Id, filename)}
                                            style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', padding: '4px 6px', borderRadius: '4px' }}
                                            title="Download"
                                        >
                                            <FontAwesomeIcon icon={faDownload} />
                                        </button>
                                        <button
                                            onClick={() => setDeleteTarget({ id: doc.Id, filename })}
                                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px 6px', borderRadius: '4px' }}
                                            title="Delete"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {deleteTarget && (
                    <div className="modal-overlay" style={{ zIndex: 1100 }}>
                        <div className="modal-content" style={{ maxWidth: '420px' }}>
                            <div className="modal-header">
                                <h3 style={{ margin: 0 }}>Delete Document</h3>
                            </div>
                            <div style={{ padding: '1.25rem' }}>
                                <p>Are you sure you want to delete <strong>{deleteTarget.filename}</strong>?</p>
                                <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>This action cannot be undone.</p>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '0 1.25rem 1.25rem' }}>
                                <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
                                <button className="btn btn-danger" onClick={handleDelete} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>Delete</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <PdfPreviewModal
                isOpen={showPdfPreview}
                pdfUrl={previewPdfUrl}
                onClose={handleClosePreview}
                fileName={previewFileName}
            />
        </div>
    );
};

export default InvoiceDocumentPanel;
